import { Flex, Spin, Tooltip } from 'antd'
import isEmpty from 'lodash-es/isEmpty'
import { FunctionComponent, ReactNode, useMemo } from 'react'
import styled from 'styled-components'
import Blur from '@/components/common/blur'
import KolDetailDataBlockSubHeader from '@/components/kol/common/kol-detail-data-block-sub-header'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import { Percentage } from '@/components/kol/detail/audience-analyses/audience-analysis'
import AudienceRegionCityRatioChart from '@/components/kol/detail/audience-analyses/audience-region-city-ratio-chart'
import Blocker from '@/components/kol/detail/blocker'
import { NoData } from '@/components/kol/detail/no-data'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Audience } from '@/store/kol-detail'
import { Platform, PlatformCode } from '@/utils/convert-platform'
import { convertToPercentage } from '@/utils/convert-to-percentage'
import { getPercentageMaximumFractionDigits } from '@/utils/i18n/get-percentage-maximum-fraction-digits'

interface AudienceLocationProps {
  audienceData?: Audience
  isFetching: boolean
  isLocked: boolean
  showMockData?: boolean
  isPublicDetailPage: boolean
  platform: Platform
}

const AudienceLocation: FunctionComponent<AudienceLocationProps> = ({
  audienceData,
  isFetching,
  isLocked,
  showMockData,
  isPublicDetailPage,
  platform,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const enabledCityData = [PlatformCode.Instagram].includes(platform.code)

  const columns = enabledCityData ? 2 : 1

  const activeBlur = isLocked && !showMockData

  const [countrySeries, citySeries] = useMemo((): {
    title: string
    percent: number
  }[][] => {
    const countrySeries =
      audienceData?.geo?.countries.slice(0, 5).map((country) => {
        return {
          title: country.name,
          percent: convertToPercentage(
            country.weight * 100,
            getPercentageMaximumFractionDigits(country.weight),
          ),
        }
      }) ?? []
    const citySeries =
      audienceData?.geo?.cities.slice(0, 5).map((city) => {
        return {
          title: city.name,
          percent: convertToPercentage(
            city.weight * 100,
            getPercentageMaximumFractionDigits(city.weight),
          ),
        }
      }) ?? []
    return [countrySeries, citySeries]
  }, [audienceData])

  const mainCountriesTooltip: string = countrySeries
    .slice(0, 3)
    .map((country) => {
      return `${country.title} ${formatNumber(country.percent / 100, {
        isPercentageDisplay: true,
      })}`
    })
    .join(', ')

  return (
    <Flex vertical gap={30}>
      <KolDetailStatistic
        loading={isFetching}
        suffix={((): ReactNode | undefined => {
          if (audienceData?.mainCountry?.weight === undefined) {
            return undefined
          }

          return (
            <Tooltip placement='right' title={mainCountriesTooltip}>
              <Percentage>
                <Blur activate={activeBlur}>
                  {formatNumber(audienceData.mainCountry.weight, {
                    isPercentageDisplay: true,
                  })}
                </Blur>
              </Percentage>
            </Tooltip>
          )
        })()}
        title={formatMessage({ id: 'kol:audience_analysis_region' })}
        value={
          audienceData?.mainCountry?.name ? (
            <Blur activate={activeBlur}>{audienceData.mainCountry.name}</Blur>
          ) : null
        }
      />
      {isEmpty(audienceData) && !isFetching ? (
        <NoData />
      ) : (
        <>
          <ChartWrapper $columns={columns}>
            <KolDetailDataBlockSubHeaderWrapper
              title={formatMessage({
                id: 'kol:audience_analysis_region_distribution',
              })}
            />
            {enabledCityData && (
              <KolDetailDataBlockSubHeaderWrapper
                title={formatMessage({
                  id: 'kol:audience_analysis_city_distribution',
                })}
              />
            )}
          </ChartWrapper>

          <Spin indicator={<Blocker />} spinning={isPublicDetailPage}>
            <ChartWrapper $columns={columns}>
              <AudienceRegionCityRatioChart
                blur={activeBlur}
                loading={isFetching}
                series={countrySeries}
              />
              {enabledCityData && (
                <AudienceRegionCityRatioChart
                  blur={activeBlur}
                  loading={isFetching}
                  series={citySeries}
                />
              )}
            </ChartWrapper>
          </Spin>
        </>
      )}
    </Flex>
  )
}

export default AudienceLocation

const ChartWrapper = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: ${(props): string => `repeat(${props.$columns}, 1fr)`};
  gap: 40px;
`

const KolDetailDataBlockSubHeaderWrapper = styled(KolDetailDataBlockSubHeader)`
  margin: 0 0 15px 0;
`
